import { createStore } from "@xstate/store";
import { useSelector } from "@xstate/store/react";

import { DocumentTypeEnumType } from "@libs/api/patients";
import { UploadedDocumentsApi } from "@/data/api/uploadedDocumentsApi";
import {
  PendingUploadType,
  UploadStatusType,
} from "@/domain/uploadedDocuments";
import { SelectedFileType } from "@/hooks/useFilePicker";
import { logger } from "@/utils/logger";

const POLLING_INTERVAL_MS = 500;
const MAX_POLLING_DURATION_MS = 15000;

// Store polling timers outside of store context since Maps don't serialize well
const pollingTimers = new Map<string, ReturnType<typeof setInterval>>();
const pollingStartTimes = new Map<string, number>();

type DocumentUploadStoreContextType = {
  pendingUploads: PendingUploadType[];
};

// Event types
type StartUploadEventType = {
  type: "startUpload";
  uploadId: string;
  documentType: DocumentTypeEnumType;
  documentCategoryId: string;
  householdMemberId: string;
  files: SelectedFileType[];
};

type UpdateStatusEventType = {
  type: "updateStatus";
  uploadId: string;
  status: UploadStatusType;
  documentId?: string;
  updatedAt?: string;
};

type RemoveUploadEventType = {
  type: "removeUpload";
  uploadId: string;
};

export const documentUploadStore = createStore({
  context: {
    pendingUploads: [],
  } as DocumentUploadStoreContextType,
  on: {
    startUpload: (context, event: StartUploadEventType) => {
      const newUpload: PendingUploadType = {
        id: event.uploadId,
        documentType: event.documentType,
        documentCategoryId: event.documentCategoryId,
        householdMemberId: event.householdMemberId,
        status: "UPLOADING",
        files: event.files.map(f => ({
          uri: f.uri,
          fileName: f.fileName,
          mimeType: f.mimeType,
        })),
      };

      return {
        ...context,
        pendingUploads: [newUpload, ...context.pendingUploads],
      };
    },

    updateStatus: (context, event: UpdateStatusEventType) => {
      return {
        ...context,
        pendingUploads: context.pendingUploads.map(upload =>
          upload.id === event.uploadId
            ? {
                ...upload,
                status: event.status,
                documentId: event.documentId ?? upload.documentId,
                updatedAt: event.updatedAt ?? upload.updatedAt,
              }
            : upload,
        ),
      };
    },

    removeUpload: (context, event: RemoveUploadEventType) => {
      stopPollingForUpload(event.uploadId);

      return {
        ...context,
        pendingUploads: context.pendingUploads.filter(
          upload => upload.id !== event.uploadId,
        ),
      };
    },

    clearCompleted: context => {
      return {
        ...context,
        pendingUploads: context.pendingUploads.filter(
          upload => upload.status !== "UPLOADED",
        ),
      };
    },

    reset: () => {
      return {
        pendingUploads: [],
      };
    },
  },
});

// Polling management functions (outside store to avoid serialization issues)
const stopPollingForUpload = (uploadId: string) => {
  const timer = pollingTimers.get(uploadId);
  if (timer) {
    clearInterval(timer);
    pollingTimers.delete(uploadId);
  }
  pollingStartTimes.delete(uploadId);
};

export const startPollingForDocument = ({
  uploadId,
  documentId,
  onComplete,
}: {
  uploadId: string;
  documentId: string;
  onComplete: () => void;
}) => {
  pollingStartTimes.set(uploadId, Date.now());

  const pollDocument = async () => {
    const startTime = pollingStartTimes.get(uploadId);
    if (!startTime) return;

    const elapsed = Date.now() - startTime;
    if (elapsed >= MAX_POLLING_DURATION_MS) {
      stopPollingForUpload(uploadId);
      return;
    }

    try {
      const document = await UploadedDocumentsApi.getDocument(documentId);

      if (!document?.files?.length) return;

      const isAllUploaded = document.files.every(
        file => file.status === "UPLOADED",
      );
      const hasFailed = document.files.some(file => file.status === "FAILED");

      if (isAllUploaded) {
        stopPollingForUpload(uploadId);
        // Remove from pending uploads since it's now in the documents list
        documentUploadStore.send({
          type: "removeUpload",
          uploadId,
        });
        onComplete();
      } else if (hasFailed) {
        stopPollingForUpload(uploadId);
        documentUploadStore.send({
          type: "updateStatus",
          uploadId,
          status: "FAILED",
        });
      }
    } catch (error) {
      logger.error({ error, message: "Error polling document status" });
    }
  };

  const timer = setInterval(() => {
    void pollDocument();
  }, POLLING_INTERVAL_MS);

  pollingTimers.set(uploadId, timer);

  // Run first poll immediately
  void pollDocument();
};

// Selectors
export const usePendingUploads = () =>
  useSelector(documentUploadStore, state => state.context.pendingUploads);

export const useIsUploading = () =>
  useSelector(documentUploadStore, state =>
    state.context.pendingUploads.some(
      upload => upload.status === "UPLOADING" || upload.status === "SCANNING",
    ),
  );

// Actions
export const startUpload = (params: Omit<StartUploadEventType, "type">) => {
  documentUploadStore.send({ type: "startUpload", ...params });
};

export const updateUploadStatus = (
  params: Omit<UpdateStatusEventType, "type">,
) => {
  documentUploadStore.send({ type: "updateStatus", ...params });
};

export const removeUpload = (uploadId: string) => {
  documentUploadStore.send({ type: "removeUpload", uploadId });
};

export const clearCompletedUploads = () => {
  documentUploadStore.send({ type: "clearCompleted" });
};

export const stopPolling = (uploadId: string) => {
  stopPollingForUpload(uploadId);
};

export const resetStore = () => {
  documentUploadStore.send({ type: "reset" });
};

// Cleanup function for unmounting - stops polling and resets store state
export const cleanupDocumentUploadStore = () => {
  pollingTimers.forEach(timer => clearInterval(timer));
  pollingTimers.clear();
  pollingStartTimes.clear();
  resetStore();
};
